import { AlertTriangle } from "lucide-react";
import type { ChangeEvent, ReactNode } from "react";

import type { GeoMetric } from "@busright/data-contract";

import type { BasemapStyle } from "@/components/geo/GeoMap";
import { type RampName, rampCssGradient } from "@/lib/color-ramp";
import { type HeightScale, SCALE_OPTIONS } from "@/lib/scale";

// Top-right control panel for the geo explorer: metric selector, height-scale toggle, peak-height
// slider, and a color legend with the live min/max.

export interface GeoControlsProps {
  readonly metrics: readonly GeoMetric[];
  readonly activeMetric: GeoMetric | undefined;
  readonly onMetricChange: (key: string) => void;
  readonly heightScale: HeightScale;
  readonly onHeightScaleChange: (scale: HeightScale) => void;
  readonly peakHeightM: number;
  readonly onPeakHeightChange: (m: number) => void;
  readonly ramp: RampName;
  readonly minValue: number;
  readonly maxValue: number;
  readonly basemap: BasemapStyle;
  readonly onBasemapChange: (basemap: BasemapStyle) => void;
}

const BASEMAP_OPTIONS: readonly { value: BasemapStyle; label: string }[] = [
  { value: "dark", label: "Dark" },
  { value: "light", label: "Light" },
];

const PEAK_MIN = 20000;
const PEAK_MAX = 400000;
const PEAK_STEP = 10000;

const fmtLegend = (v: number, unit: string): string => {
  const num = Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : v.toFixed(1);
  return unit ? `${num} ${unit}` : num;
};

export const GeoControls = ({
  metrics,
  activeMetric,
  onMetricChange,
  heightScale,
  onHeightScaleChange,
  peakHeightM,
  onPeakHeightChange,
  ramp,
  minValue,
  maxValue,
  basemap,
  onBasemapChange,
}: GeoControlsProps): ReactNode => {
  const unit = activeMetric?.unit ?? "";

  return (
    <div className="geo-controls">
      <div className="geo-control-group">
        <span className="geo-group-label">Basemap</span>
        <div className="geo-seg">
          {BASEMAP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={basemap === opt.value ? "active" : undefined}
              onClick={() => onBasemapChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="geo-control-group">
        <label htmlFor="geo-metric">Metric</label>
        <select
          id="geo-metric"
          value={activeMetric?.key ?? ""}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => onMetricChange(e.target.value)}
        >
          {metrics.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
              {m.unit ? ` (${m.unit})` : ""}
            </option>
          ))}
        </select>
        {activeMetric ? <p className="geo-metric-desc">{activeMetric.description}</p> : null}
        {activeMetric && activeMetric.fillPct < 80 ? (
          <p className="geo-metric-warn">
            <AlertTriangle size={12} aria-hidden /> Sparse: only {activeMetric.fillPct.toFixed(0)}%
            of pings carry this value.
          </p>
        ) : null}
      </div>

      <div className="geo-control-group">
        <span className="geo-group-label">Height scale</span>
        <div className="geo-seg">
          {SCALE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={heightScale === opt.value ? "active" : undefined}
              onClick={() => onHeightScaleChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="geo-control-group">
        <label htmlFor="geo-peak">Peak height</label>
        <input
          id="geo-peak"
          type="range"
          min={PEAK_MIN}
          max={PEAK_MAX}
          step={PEAK_STEP}
          value={peakHeightM}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onPeakHeightChange(Number(e.target.value))
          }
        />
      </div>

      <div className="geo-control-group">
        <span className="geo-group-label">Legend</span>
        <div className="geo-legend-bar" style={{ background: rampCssGradient(ramp) }} />
        <div className="geo-legend-scale br-tabular-nums">
          <span>{fmtLegend(minValue, unit)}</span>
          <span>{fmtLegend(maxValue, unit)}</span>
        </div>
      </div>
    </div>
  );
};
